import React, { useEffect, useMemo, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import api from "@/lib/api";
import { formatNumber } from "@/lib/format";

/** Iter v34dc — Rekap bulanan material masuk vs keluar (Batching Plant). */
export const StockMonthlyChart = () => {
  const [materials, setMaterials] = useState([]);
  const [materialId, setMaterialId] = useState("");
  const [months, setMonths] = useState(12);
  const [unit, setUnit] = useState("kg");
  const [data, setData] = useState({ months: [], totals: {} });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/batching/materials").then((r) => setMaterials(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get("/batching/stock-monthly", { params: { months, ...(materialId ? { material_id: materialId } : {}) } })
      .then((r) => setData(r.data || { months: [], totals: {} }))
      .catch(() => setData({ months: [], totals: {} }))
      .finally(() => setLoading(false));
  }, [months, materialId]);

  const unitLabel = unit === "kg" ? "kg" : "m³";
  const rows = useMemo(
    () => (data.months || []).map((m) => ({
      label: m.label,
      masuk: unit === "kg" ? m.in_kg : m.in_m3,
      keluar: unit === "kg" ? m.out_kg : m.out_m3,
    })),
    [data, unit],
  );
  const totals = data.totals || {};
  const tIn = unit === "kg" ? totals.in_kg : totals.in_m3;
  const tOut = unit === "kg" ? totals.out_kg : totals.out_m3;
  const tNet = unit === "kg" ? totals.net_kg : totals.net_m3;
  const dec = unit === "kg" ? 0 : 2;
  const hasData = rows.some((r) => r.masuk || r.keluar);

  return (
    <div className="rounded-3xl bg-white border border-stone-200/70 p-5 md:p-6" data-testid="stock-monthly-chart">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-stone-500 font-semibold">Rekap Stok Bulanan</div>
          <h2 className="font-display text-base md:text-lg text-stone-900 mt-0.5">Material Masuk vs Keluar</h2>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={materialId}
            onChange={(e) => setMaterialId(e.target.value)}
            className="border border-stone-300 rounded-lg px-2 py-1.5 text-xs bg-white outline-none focus:border-stone-800"
            data-testid="chart-material-filter"
          >
            <option value="">Semua Material</option>
            {materials.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          <div className="inline-flex border border-stone-300 overflow-hidden rounded-lg" data-testid="chart-months-toggle">
            {[6, 12, 24].map((n) => (
              <button key={n} type="button" onClick={() => setMonths(n)}
                className={`px-2.5 py-1.5 text-xs font-semibold transition-colors ${months === n ? "bg-stone-800 text-white" : "bg-white text-stone-600 hover:bg-stone-100"}`}
                data-testid={`chart-months-${n}`}>{n}B</button>
            ))}
          </div>
          <div className="inline-flex border border-stone-300 overflow-hidden rounded-lg" data-testid="chart-unit-toggle">
            {["kg", "m3"].map((u) => (
              <button key={u} type="button" onClick={() => setUnit(u)}
                className={`px-2.5 py-1.5 text-xs font-semibold transition-colors ${unit === u ? "bg-stone-800 text-white" : "bg-white text-stone-600 hover:bg-stone-100"}`}
                data-testid={`chart-unit-${u}`}>{u === "kg" ? "kg" : "m³"}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-5">
        <Stat label="Total Masuk" value={`${formatNumber(tIn || 0, dec)} ${unitLabel}`} tone="emerald" testid="chart-total-in" />
        <Stat label="Total Keluar" value={`${formatNumber(tOut || 0, dec)} ${unitLabel}`} tone="red" testid="chart-total-out" />
        <Stat label="Selisih" value={`${formatNumber(tNet || 0, dec)} ${unitLabel}`} tone="stone" testid="chart-total-net" />
      </div>

      <div className="h-72" data-testid="chart-canvas">
        {loading ? (
          <div className="h-full flex items-center justify-center text-xs text-stone-400">Memuat grafik…</div>
        ) : !hasData ? (
          <div className="h-full flex items-center justify-center text-xs text-stone-400" data-testid="chart-empty">
            Belum ada data stok pada rentang ini.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barGap={4}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#78716c" }} tickLine={false} axisLine={{ stroke: "#e7e5e4" }} />
              <YAxis tick={{ fontSize: 11, fill: "#78716c" }} tickLine={false} axisLine={false}
                tickFormatter={(v) => formatNumber(v, 0)} width={70} />
              <Tooltip
                formatter={(v, name) => [`${formatNumber(v, dec)} ${unitLabel}`, name === "masuk" ? "Masuk" : "Keluar"]}
                contentStyle={{ borderRadius: 12, border: "1px solid #e7e5e4", fontSize: 12 }}
              />
              <Legend formatter={(v) => (v === "masuk" ? "Masuk" : "Keluar")} wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="masuk" fill="#059669" radius={[4, 4, 0, 0]} maxBarSize={28} />
              <Bar dataKey="keluar" fill="#dc2626" radius={[4, 4, 0, 0]} maxBarSize={28} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

const TONES = {
  emerald: "border-l-emerald-500 text-emerald-700",
  red: "border-l-red-500 text-red-700",
  stone: "border-l-stone-800 text-stone-900",
};

function Stat({ label, value, tone, testid }) {
  return (
    <div className={`border-l-4 pl-3 ${TONES[tone]}`}>
      <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">{label}</div>
      <div className="font-mono-num text-lg font-bold" data-testid={testid}>{value}</div>
    </div>
  );
}

export default StockMonthlyChart;
